import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { MachineNameAndSourceId } from "@common/types/server-list-types";
import { useI18N } from "@src/language/language-context";
import {
  MultipleTextValuesRow,
  QueryBinding,
  Widget,
} from "@src/types/dashboards";
import {
  selectUnitsById,
  selectUnitsStatus,
  unitsFailed,
  unitsLoaded,
  unitsRequested,
} from "@src/reducers/units-slice";
import { findUnitByReference } from "@src/cloud-connect/units/utils/find-unit-by-reference";
import { unitOptionLabelOf } from "@src/cloud-connect/units/utils/unit-option-label-of";
import { getAllSourcesListUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-all-sources-list-use-case";
import { getActiveTimeseriesUseCase } from "@src/cloud-connect/timeseries/use-cases/get-active-timeseries-use-case";
import { getUnitsUseCase } from "@src/cloud-connect/timeseries/use-cases/get-units-use-case";
import {
  getNumericNodeUnit,
  toNumericAndStringNodeOptions,
} from "@src/components/organisms/widgets/node-options";
import {
  MULTIPLE_TEXT_VALUES_MAX_ENTRIES,
  MULTIPLE_TEXT_VALUES_MIN_ENTRIES,
} from "@src/components/organisms/widgets/multiple-text-values/multiple-text-values-height";
import {
  buildScaleTransformsForEdits,
  scaleForField,
} from "@src/components/organisms/widgets/utils/value-scaling";
import { parseUserNumber } from "@src/components/organisms/widgets/utils/parse-user-number";
import {
  ColorSwatchPicker,
  DEFAULT_LABEL_COLOR,
  DEFAULT_TREND_LINE_COLOR,
  TREND_LINE_PALETTE,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import {
  DEFAULT_POLLING_INTERVAL,
  DURATION_GAUGE_WIDGET_MS,
  durationToIso,
  isoToDurationMs,
} from "@src/components/organisms/widgets/constants";
import plusCircleIcon from "@src/assets/svg/plus-circle-icon.svg";
import { LookbackAndPollingFields } from "@src/components/molecules/lookback-and-polling-fields/lookback-and-polling-fields";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import Spinner from "@src/components/atoms/spinner/spinner";
import {
  MultipleTextValuesEntryCard,
  MultipleTextValuesEntryDraft,
} from "./multiple-text-values-entry-card";
import { useSaveWidget } from "../hooks/use-save-widget";
import styles from "./multiple-text-values-editor.module.scss";

const cssClassName = "multiple-text-values-editor";

const NS = "modules.multipleTextValues";

interface MultipleTextValuesEditorProps {
  widget: Widget;
  /** Brand-new widget (not committed yet). Its `encoding.rows` are the
   *  template's defaults, so their labels are defaults too, not edits. */
  isPendingWidget: boolean;
  onClose: () => void;
}

const newEntryId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `entry-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Seed one entry per saved row, joining each row back to its binding (the
 *  address lives on `query.bindings[]`, matched by `field`). The name shown
 *  for a machine/node starts as its id and is swapped for the human label
 *  once that machine's node list lands. */
const seedEntries = (
  widget: Widget,
  isPendingWidget: boolean,
): MultipleTextValuesEntryDraft[] => {
  const rows = widget.encoding?.rows ?? [];
  const bindingByField = new Map(
    (widget.query?.bindings ?? []).map((binding) => [binding.field, binding]),
  );
  const transforms = widget.encoding?.transforms;
  const drafts = rows.map((row) => {
    const binding = bindingByField.get(row.field);
    const machineId = binding?.source ?? widget.query?.source ?? null;
    const address = binding?.address ?? null;
    const label = row.label ?? "";
    return {
      id: row.id,
      machine: machineId ? { name: machineId, source_id: machineId } : null,
      // Only seed the node when its machine resolved too — a node without a
      // machine would save a binding with an empty `source` that can never
      // fetch, and the entry's node picker would look filled in.
      node: machineId && address ? { name: address, source_id: address } : null,
      label,
      shouldAutoFillLabel: isPendingWidget || label.trim() === "",
      unit: row.unit ?? null,
      // Show the factor this row is currently rendered with, read back from
      // the widget's saved transforms.
      scale: String(scaleForField(row.field, transforms)),
    };
  });
  return drafts.length > 0 ? drafts : [emptyEntry()];
};

const emptyEntry = (): MultipleTextValuesEntryDraft => ({
  id: newEntryId(),
  machine: null,
  node: null,
  label: "",
  shouldAutoFillLabel: true,
  unit: null,
  // 1 is the default factor. The save drops it, so an entry the user never
  // scaled writes nothing into `transforms`.
  scale: "1",
});

export const MultipleTextValuesEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: MultipleTextValuesEditorProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();
  const units = useSelector(selectUnitsById);
  const unitsStatus = useSelector(selectUnitsStatus);
  const isLoadingUnits = unitsStatus === "idle" || unitsStatus === "loading";

  const [entries, setEntries] = useState<MultipleTextValuesEntryDraft[]>(() =>
    seedEntries(widget, isPendingWidget),
  );
  // Per-machine node lists. The editor fetches these itself: each entry
  // picks its own machine, so the drawer's single-machine node list doesn't
  // apply. `undefined` means "not loaded yet" → the node dropdown shows its
  // loading state.
  const [nodesByMachine, setNodesByMachine] = useState<
    Record<string, MachineNameAndSourceId[]>
  >({});
  // Per-machine node → unit reference, so picking a node can auto-fill the
  // entry's unit without a second request. Same shape the drawer builds for
  // machine-overview's rows.
  const [nodeUnitsByMachine, setNodeUnitsByMachine] = useState<
    Record<string, Record<string, number | null>>
  >({});

  const [color, setColor] = useState<string | null>(
    widget.presentation?.color ?? null,
  );
  const [labelColor, setLabelColor] = useState<string | null>(
    widget.presentation?.label_color ?? null,
  );
  const [isUsingCustomColors, setIsUsingCustomColors] = useState(
    (widget.presentation?.color ?? null) != null ||
      (widget.presentation?.label_color ?? null) != null,
  );
  const [lookbackMs, setLookbackMs] = useState<number | null>(
    isoToDurationMs(widget.query?.window?.duration) ?? DURATION_GAUGE_WIDGET_MS,
  );
  const [pollingInterval, setPollingInterval] = useState<string | null>(
    widget.query?.sampling?.polling_interval ?? DEFAULT_POLLING_INTERVAL,
  );

  const [machineOptions, setMachineOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);

  // Load machines once.
  useEffect(() => {
    setIsLoadingMachines(true);
    getAllSourcesListUseCase().then((result) => {
      if (result.success && result.data) {
        setMachineOptions(
          Object.values(result.data).map((s) => ({
            name: s.name,
            source_id: s.source_id,
          })),
        );
      } else {
        setMachineOptions([]);
        console.error("Failed to load machines:", result.error);
      }
      setIsLoadingMachines(false);
    });
  }, []);

  // Units come from the global slice (loaded at dashboards-load). Lazy
  // fallback: if the slice was never attempted, fetch once so the dropdown
  // isn't empty.
  const unitsAttempted = useRef(false);
  useEffect(() => {
    if (unitsStatus !== "idle" || unitsAttempted.current) return;
    unitsAttempted.current = true;
    dispatch(unitsRequested());
    getUnitsUseCase().then((result) => {
      if (result.success && result.data) {
        dispatch(unitsLoaded(result.data));
      } else {
        dispatch(unitsFailed(result.error ?? "Failed to fetch units"));
      }
    });
  }, [unitsStatus, dispatch]);

  const unitOptions = useMemo(
    () =>
      Object.values(units).map((value) => {
        const text =
          value.unit_text?.eng ?? Object.values(value.unit_text ?? {})[0];
        return {
          name: unitOptionLabelOf(text, value.unit_id),
          source_id: value.unit_id,
        };
      }),
    [units],
  );

  // One active-timeseries call per machine in use, cached by machine id. The
  // key is a stable string so re-ordering entries doesn't re-fetch.
  const machinesKey = entries
    .map((entry) => entry.machine?.source_id ?? "_")
    .join("|");
  useEffect(() => {
    const machineIds = Array.from(
      new Set(
        machinesKey
          .split("|")
          .filter((id): id is string => Boolean(id) && id !== "_"),
      ),
    ).filter((id) => nodesByMachine[id] === undefined);
    if (machineIds.length === 0) return;

    let cancelled = false;
    void Promise.all(
      machineIds.map(async (id) => ({
        id,
        result: await getActiveTimeseriesUseCase(id),
      })),
    ).then((results) => {
      if (cancelled) return;
      setNodesByMachine((prev) => {
        const next = { ...prev };
        for (const { id, result } of results) {
          if (next[id] !== undefined) continue;
          next[id] =
            result.success && result.data
              ? toNumericAndStringNodeOptions(result.data.timeseries)
              : [];
        }
        return next;
      });
      setNodeUnitsByMachine((prev) => {
        const next = { ...prev };
        for (const { id, result } of results) {
          if (next[id] !== undefined) continue;
          next[id] =
            result.success && result.data
              ? Object.fromEntries(
                  Object.values(result.data.timeseries).map((timeseries) => [
                    timeseries.source,
                    getNumericNodeUnit(
                      result.data!.timeseries,
                      timeseries.source,
                    ),
                  ]),
                )
              : {};
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [machinesKey, nodesByMachine]);

  // Swap each entry's placeholder machine/node names for the human labels
  // once that machine's node list resolves. `source_id` stays the address so
  // the saved bindings are unchanged.
  useEffect(() => {
    setEntries((prev) => {
      let dirty = false;
      const next = prev.map((entry) => {
        const options = entry.machine
          ? nodesByMachine[entry.machine.source_id]
          : undefined;
        if (!options) return entry;
        const machineMatch = machineOptions.find(
          (option) => option.source_id === entry.machine?.source_id,
        );
        const nodeMatch = entry.node
          ? options.find((option) => option.source_id === entry.node?.source_id)
          : undefined;
        const machine = machineMatch ?? entry.machine;
        const node = nodeMatch ?? entry.node;
        if (machine === entry.machine && node === entry.node) return entry;
        dirty = true;
        return { ...entry, machine, node };
      });
      return dirty ? next : prev;
    });
  }, [nodesByMachine, machineOptions]);

  // Once the machines list, the unit catalog and every entry's machine node
  // list are in, reveal the form — otherwise the machine + node would open on
  // the raw ids the seed carries and swap to their names a moment later.
  useEffect(() => {
    if (!isInitializing) return;
    const rowsLoaded = entries.every(
      (entry) =>
        !entry.machine || nodesByMachine[entry.machine.source_id] !== undefined,
    );
    if (!isLoadingMachines && !isLoadingUnits && rowsLoaded) {
      setIsInitializing(false);
    }
  }, [
    isLoadingMachines,
    isLoadingUnits,
    nodesByMachine,
    entries,
    isInitializing,
  ]);

  const updateEntry = (
    id: string,
    patch: Partial<MultipleTextValuesEntryDraft>,
  ): void => {
    setEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
    );
  };

  // Resolve a node's unit id from the per-machine reference map. Null when
  // the node declares no unit (a string node, or a numeric one without one).
  const resolveUnitForNode = (
    machineId: string | undefined,
    nodeId: string | undefined,
  ): string | null => {
    if (!machineId || !nodeId) return null;
    const reference = nodeUnitsByMachine[machineId]?.[nodeId] ?? null;
    if (reference == null) return null;
    return findUnitByReference(units, reference)?.unit_id ?? null;
  };

  const changeMachine = (
    entry: MultipleTextValuesEntryDraft,
    machine: MachineNameAndSourceId | null,
  ): void => {
    // A machine change invalidates the node — the old address almost
    // certainly doesn't exist on the new machine — so the unit it drove is
    // cleared with it.
    updateEntry(entry.id, {
      machine,
      node: null,
      unit: null,
    });
  };

  const changeNode = (
    entry: MultipleTextValuesEntryDraft,
    node: MachineNameAndSourceId | null,
  ): void => {
    const patch: Partial<MultipleTextValuesEntryDraft> = {
      node,
      unit: resolveUnitForNode(entry.machine?.source_id, node?.source_id),
    };
    if (entry.shouldAutoFillLabel) patch.label = node?.name ?? "";
    updateEntry(entry.id, patch);
  };

  const changeLabel = (
    entry: MultipleTextValuesEntryDraft,
    label: string,
  ): void => {
    updateEntry(entry.id, { label, shouldAutoFillLabel: false });
  };

  // A pick and a clear both stop the auto-fill; only a new node starts it
  // again (see `changeNode`).
  const changeUnit = (
    entry: MultipleTextValuesEntryDraft,
    unit: MachineNameAndSourceId | null,
  ): void => {
    updateEntry(entry.id, { unit: unit?.source_id ?? null });
  };

  const changeScale = (
    entry: MultipleTextValuesEntryDraft,
    scale: string,
  ): void => {
    updateEntry(entry.id, { scale });
  };

  const addEntry = (): void => {
    setEntries((prev) =>
      prev.length >= MULTIPLE_TEXT_VALUES_MAX_ENTRIES
        ? prev
        : [...prev, emptyEntry()],
    );
  };

  const removeEntry = (id: string): void => {
    setEntries((prev) =>
      prev.length <= 1 ? prev : prev.filter((entry) => entry.id !== id),
    );
  };

  const validate = (): string | null => {
    for (const entry of entries) {
      if (!entry.node?.source_id) {
        return i18n(`${NS}.nodeRequired`);
      }
      if (entry.label.trim() === "") {
        return i18n(`${NS}.labelRequired`);
      }
      // A blank box means "no scale" and saves nothing. A box with text in it
      // must parse to a number, otherwise the save is blocked.
      if (entry.scale.trim() !== "" && parseUserNumber(entry.scale) === null) {
        return i18n(`${NS}.scaleInvalid`);
      }
    }
    if (lookbackMs == null) {
      return i18n(`${NS}.lookbackRequired`);
    }
    return null;
  };

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    // Rows are re-numbered from their render order, so every `field` matches
    // the binding built at the same index.
    const rows: MultipleTextValuesRow[] = entries.map((entry, index) => ({
      id: entry.id,
      field: `value_${index}`,
      label: entry.label,
      order: index,
      unit: entry.unit,
    }));
    // Every binding carries its own machine, so one tile can read from
    // different sources. `fields` declares each bound field, and the top-level
    // `source` mirrors the first binding for legacy readers.
    const bindings: QueryBinding[] = entries.map((entry, index) => ({
      field: `value_${index}`,
      source: entry.machine?.source_id ?? "",
      address: entry.node?.source_id ?? "",
    }));

    const updated: Widget = {
      ...widget,
      // multiple-text-values names itself through its entry labels, so its
      // title is whatever the template gave it.
      title: widget.title,
      image_url: widget.image_url ?? null,
      query: {
        ...(widget.query ?? {}),
        source: bindings[0]?.source ?? "",
        shape: "scalar" as const,
        fields: Object.fromEntries(
          bindings.map((binding) => [binding.field, "float" as const]),
        ),
        bindings,
        // Duration as ISO so a re-open rehydrates the same value.
        window: {
          duration: durationToIso(lookbackMs ?? DURATION_GAUGE_WIDGET_MS),
        },
        sampling: {
          frequency: "raw",
          polling_interval: pollingInterval ?? DEFAULT_POLLING_INTERVAL,
        },
      },
      // No threshold, no target, no bounds — the widget renders a table of
      // label + value + unit rows. Pass the saved semantics through so
      // BE-stored extras round-trip cleanly.
      semantics: { ...(widget.semantics as Record<string, unknown>) },
      encoding: {
        ...widget.encoding,
        rows,
        // Floor / ceiling contract, mirroring what the BE template declares
        // and what this editor enforces in the UI.
        min_roles: MULTIPLE_TEXT_VALUES_MIN_ENTRIES,
        max_roles: MULTIPLE_TEXT_VALUES_MAX_ENTRIES,
        // Rebuild the scale transforms for this widget. The helper claims
        // every `value_<index>` the widget had saved plus every entry on
        // screen now, so a deleted entry's old factor is dropped instead of
        // left behind. Factors of 1 are dropped, so a widget nobody scaled
        // keeps the same transforms it had before scaling existed.
        ...buildScaleTransformsForEdits(
          widget.encoding?.transforms,
          widget.query?.bindings?.length ?? 0,
          entries,
        ),
      },
      presentation: (() => {
        // Box unticked → both colour keys are stripped so the rows fall back
        // to the dashboard theme. Same contract as single-text-value, but
        // there is no widget-level `unit` / `label` here: both are per entry
        // and ride on `encoding.rows`.
        const withoutColors = Object.fromEntries(
          Object.entries(widget.presentation ?? {}).filter(
            ([k]) => k !== "color" && k !== "label_color",
          ),
        );
        return {
          ...withoutColors,
          ...(isUsingCustomColors && {
            label_color: labelColor ?? DEFAULT_LABEL_COLOR,
            color: color ?? DEFAULT_TREND_LINE_COLOR,
          }),
        };
      })(),
    };
    // The tile is content-sized, so its height follows the entry count.
    save(updated, { validate, contentRowCount: rows.length });
  };

  const texts = {
    entryLabel: i18n(`${NS}.entryLabel`),
    entryLabelPlaceholder: i18n(`${NS}.entryLabelPlaceholder`),
    machineLabel: i18n(`${NS}.machineLabel`),
    noMachines: i18n(`${NS}.noMachines`),
    machinesPlaceholder: i18n(`${NS}.machinesPlaceholder`),
    nodeLabel: i18n(`${NS}.nodeLabel`),
    noNodes: i18n(`${NS}.noNodes`),
    nodesPlaceholder: i18n(`${NS}.nodesPlaceholder`),
    unitLabel: i18n(`${NS}.unitLabel`),
    noUnits: i18n(`${NS}.noUnits`),
    unitsPlaceholder: i18n(`${NS}.unitsPlaceholder`),
    removeEntry: i18n(`${NS}.removeEntry`),
    saveButton: i18n(`${NS}.saveButton`),
    cancelButton: i18n(`${NS}.cancelButton`),
  };

  if (isInitializing) {
    return (
      <div className={styles[`${cssClassName}__loading`]}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <span className={styles[`${cssClassName}__toggle-label`]}>
        {i18n(`${NS}.entriesLabel`)}
      </span>
      {entries.map((entry, index) => (
        <MultipleTextValuesEntryCard
          key={entry.id}
          entry={entry}
          index={index + 1}
          machineOptions={machineOptions}
          nodeOptions={
            entry.machine ? nodesByMachine[entry.machine.source_id] ?? [] : []
          }
          unitOptions={unitOptions}
          isLoadingMachines={isLoadingMachines}
          isLoadingNodes={
            entry.machine != null &&
            nodesByMachine[entry.machine.source_id] === undefined
          }
          isLoadingUnits={false}
          removable={entries.length > 1}
          onMachineChange={(machine) => changeMachine(entry, machine)}
          onNodeChange={(node) => changeNode(entry, node)}
          onLabelChange={(label) => changeLabel(entry, label)}
          onUnitChange={(unit) => changeUnit(entry, unit)}
          onScaleChange={(scale) => changeScale(entry, scale)}
          onRemove={() => removeEntry(entry.id)}
          texts={texts}
        />
      ))}
      {entries.length < MULTIPLE_TEXT_VALUES_MAX_ENTRIES && (
        <button
          type="button"
          className={styles[`${cssClassName}__add-line`]}
          onClick={addEntry}
          aria-label={i18n(`${NS}.addEntry`)}
        >
          <img
            src={plusCircleIcon}
            alt=""
            className={styles[`${cssClassName}__plus-icon`]}
          />
          {i18n(`${NS}.addEntry`)}
        </button>
      )}

      <LookbackAndPollingFields
        i18n={i18n}
        lookbackMs={lookbackMs}
        setLookbackMs={setLookbackMs}
        pollingInterval={pollingInterval}
        setPollingInterval={setPollingInterval}
      />

      <CheckBoxWithText
        text={i18n(`${NS}.customColorsCheckbox`)}
        checkboxState={isUsingCustomColors}
        handleCheckBoxClick={() => {
          const next = !isUsingCustomColors;
          setIsUsingCustomColors(next);
          if (next) {
            setColor((current) => current ?? DEFAULT_TREND_LINE_COLOR);
            setLabelColor((current) => current ?? DEFAULT_LABEL_COLOR);
          } else {
            setColor(null);
            setLabelColor(null);
          }
        }}
      />
      {isUsingCustomColors && (
        <>
          <div className={styles[`${cssClassName}__field`]}>
            <ColorSwatchPicker
              label={i18n(`${NS}.labelColorLabel`)}
              value={labelColor}
              swatches={TREND_LINE_PALETTE}
              onChange={setLabelColor}
            />
          </div>
          <div className={styles[`${cssClassName}__field`]}>
            <ColorSwatchPicker
              label={i18n(`${NS}.valueColorLabel`)}
              value={color}
              swatches={TREND_LINE_PALETTE}
              onChange={setColor}
            />
          </div>
        </>
      )}

      <Spacer16 />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={texts.saveButton}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={texts.cancelButton}
          variant="secondary"
          // Cancel must NOT fire mid-save: it would dispatch
          // removePendingWidget while POST/PUT are still in flight and leak a
          // server-side orphan.
          disabled={isSaving}
        />
      </FlexRowContainer>
    </>
  );
};
